/**
 * GlobalState.auth 切片与 Worker 数据源接线（任务 6c）。
 *
 * - `unknown` 是首帧初值：凭证在 Worker 的 IndexedDB 里，主线程要等 auth/state
 *   回包才知道登录与否。守卫（6d）对 unknown 不跳转——避免二次启动闪登录页。
 * - 'auth/lost' 推送 = Worker 已清凭证（刷新失败/重放仍失效），置 logged-out
 *   并带 lostReason（登录页提示「登录已过期」用）。
 * - sync() 暴露给崩溃重启路径（appWorker 的 onRestart）重新对齐快照。
 */

import type { GlobalStore } from './global';
import type { WorkerHost } from './workerHost';
import type { AuthLostReason, AuthUser, WorkerMethods, WorkerPushes } from '~/worker/contract';

export type AppWorkerHost = WorkerHost<WorkerMethods, WorkerPushes>;

export type AuthSlice =
  | { readonly status: 'unknown' }
  | { readonly status: 'logged-out'; readonly lostReason?: AuthLostReason }
  | ({ readonly status: 'logged-in' } & AuthUser);

export const AUTH_UNKNOWN: AuthSlice = { status: 'unknown' };

export interface AuthConnection {
  /** 重新从 Worker 拉鉴权快照（崩溃重启后 / 登录动作完成后调用方同步） */
  sync(): Promise<void>;
  dispose(): void;
}

export function connectAuth(store: GlobalStore, host: AppWorkerHost): AuthConnection {
  const offLost = host.onPush('auth/lost', ({ reason }) => {
    store.set({ auth: { status: 'logged-out', lostReason: reason } });
  });

  async function sync(): Promise<void> {
    try {
      const state = await host.call('auth/state', null);
      if (state.status === 'logged-out') {
        const migrated = await tryMigrateLegacy(host);
        if (migrated !== null) {
          store.set({ auth: migrated });
          return;
        }
      }
      store.set({ auth: state });
    } catch {
      // Worker 初始化失败：保持现值（unknown 不误跳登录页）；崩溃重启后 onRestart 再 sync
    }
  }

  void sync();
  return { sync, dispose: offLost };
}

/** 粗筛用的字面量（与 legacyMigration.ts 保持一致；抽常量会把那个模块拖进首屏） */
const LEGACY_FLAG = 'lhim/legacy-migrated';
const LEGACY_CONTAINER = 'GetStorage';
const LEGACY_REFRESH = 'refresh_token';

/**
 * 旧版 Web 登录态迁移（21k）的首屏入口：**只做零成本粗筛**，真正的解析与 RPC
 * 在 `./legacyMigration` 里动态 import——首屏 JS 预算已贴 80KB 顶，迁移这条
 * 一次性链路不该占首屏。粗筛条件与 `readLegacyCredentials` 的前置判断一致。
 *
 * 只在 auth 判定 logged-out 时调用，有本地凭证时连 localStorage 都不读
 * （二次启动零 HTTP 路径不受影响，requests §3）。
 */
async function tryMigrateLegacy(host: AppWorkerHost): Promise<AuthSlice | null> {
  const storage = typeof localStorage === 'undefined' ? null : localStorage;
  if (storage === null) return null;
  try {
    if (storage.getItem(LEGACY_FLAG) !== null) return null;
    if (storage.getItem(LEGACY_CONTAINER) === null && storage.getItem(LEGACY_REFRESH) === null) {
      return null;
    }
  } catch {
    return null; // Safari 隐私模式等 localStorage 抛异常的环境：当作没有
  }
  const { runLegacyMigration } = await import('./legacyMigration');
  return runLegacyMigration(host, storage);
}
